import { useState, useRef } from "react";
import { Modal, Badge } from "antd";
import { BellOutlined } from "@ant-design/icons";

const TYPES = ["blue", "green", "red", "yellow"];
const ICONS = ["📦", "✅", "⚠️", "🗓️", "👤", "💰", "🔔", "📋"];

const iconBgPanel = { blue: "#1e3a5f", green: "#14311f", red: "#3b1515", yellow: "#2d2208" };
const iconBgModal = { blue: "#eff6ff", green: "#f0fdf4", red: "#fff5f5", yellow: "#fffbeb" };

const randomPick = (arr, seed) => arr[seed % arr.length];

// ── NOTIF ROW ─────────────────────────────────────────────────────────────────
const NotifRow = ({ n, index, mode = "panel" }) => {
  const isPanel = mode === "panel";
  const type = randomPick(TYPES, index);
  const icon = randomPick(ICONS, index + 3);
  const bg   = isPanel ? iconBgPanel[type] : iconBgModal[type];

  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: isPanel ? 8 : 13,
      padding: isPanel ? "10px 12px" : "14px 20px",
      borderBottom: `1px solid ${isPanel ? "#1a2235" : "#f8f9fa"}`,
      cursor: "pointer", position: "relative",
      background: "transparent",
      transition: "background 0.15s",
    }}>
      <div style={{
        width: isPanel ? 28 : 40, height: isPanel ? 28 : 40,
        borderRadius: isPanel ? 8 : 12, background: bg,
        display: "grid", placeItems: "center",
        fontSize: isPanel ? 13 : 18, flexShrink: 0,
      }}>{icon}</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: isPanel ? 11.5 : 13, fontWeight: 600, color: isPanel ? "#c9d6e8" : "#111" }}>
          {n.title}
        </div>
        {mode !== 'panel' &&<div style={{
          fontSize: isPanel ? 10.5 : 12, color: "#64748b", marginTop: 2, lineHeight: 1.5,
          ...(isPanel ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}),
        }}>
          {n.message}
        </div>}
      </div>
    </div>
  );
};

// ── PANEL HEADER ──────────────────────────────────────────────────────────────
const PanelHeader = ({ count }) => (
  <div className="flex items-center px-3 py-2.5 border-b border-[#1e2533]">
    <div className="flex items-center gap-2 text-[#e2e8f0] text-xs font-bold">
      🔔 Notifications
      {count > 0 && (
        <span className="bg-red-500 text-white text-[9.5px] font-extrabold px-1.5 py-px rounded-full float-right">
          {count}
        </span>
      )}
    </div>
  </div>
);

// ── PANEL FOOTER ──────────────────────────────────────────────────────────────
const PanelFooter = ({ onViewAll }) => (
  <div className="px-3 py-2 border-t border-[#1e2533] text-center">
    <button
      onClick={onViewAll}
      className="text-[11px] font-semibold text-blue-400 hover:bg-[#1a2744] w-full py-1 rounded-md transition-colors"
    >
      View all notifications →
    </button>
  </div>
);

// ── MODAL ─────────────────────────────────────────────────────────────────────
const NotifModal = ({ open, onClose, notifications }) => (
  <Modal
  className="mt-[5%]"
    open={open}
    onCancel={onClose}
    footer={null}
    width={520}
    styles={{ body: { padding: 0 }}}
    title={
      <div className="flex items-center gap-2 ">
        🔔 All Notifications
        {notifications.length > 0 && (
          <span className="bg-red-500 text-white text-[10px] font-extrabold px-2 py-px rounded-full">
            {notifications.length}
          </span>
        )}
      </div>
    }
  >
    <div className="max-h-[55vh] overflow-y-auto">
      {notifications.length === 0
        ? <p className="text-center text-slate-400 text-sm py-8">No notifications</p>
        : notifications.map((n, i) => <NotifRow key={i} n={n} index={i} mode="modal" />)
      }
    </div>
  </Modal>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// Props:
//   notifications  – array of { title, message }
//   collapsed      – boolean (sidebar collapsed state)

const NotificationManager = ({
  notifications = [],
  collapsed = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const hideTimer = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(hideTimer.current);
    setShowPopup(true);
  };

  const handleMouseLeave = () => {
    hideTimer.current = setTimeout(() => setShowPopup(false), 120);
  };

  const modal = (
    <NotifModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      notifications={notifications}
    />
  );

  // ── COLLAPSED: bell icon + hover popup ────────────────────────────────────
  if (collapsed) return (
    <div
      className="relative mx-auto my-2.5"
      style={{ width: 40 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="w-10 h-10 rounded-xl bg-[#161d2e] border border-[#1e2d45] flex items-center justify-center cursor-pointer hover:bg-[#1e2d45] transition-colors">
        <Badge count={notifications.length} size="small" offset={[4, -4]}>
          <BellOutlined style={{ color: "#8b9ab5", fontSize: 17 }} />
        </Badge>
      </div>

      {showPopup && (
        <div
          className="fixed w-64 bg-[#161d2e] border border-[#1e2d45] rounded-xl overflow-hidden shadow-2xl"
          style={{ left: 72, zIndex: 999 }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <PanelHeader count={notifications.length} />
          <div style={{ maxHeight: 240, overflowY: "auto" }}>
            {notifications.slice(0, 3).map((n, i) => <NotifRow key={i} n={n} index={i} mode="panel" />)}
          </div>
          <PanelFooter onViewAll={() => { setShowPopup(false); setModalOpen(true); }} />
        </div>
      )}

      {modal}
    </div>
  );

  // ── EXPANDED: inline panel ────────────────────────────────────────────────
  return (
    <>
      <div className="mx-2.5  bg-[#161d2e] border border-[#1e2d45] rounded-xl overflow-hidden">
        <PanelHeader count={notifications.length} />
        <div style={{ maxHeight: 160, overflowY: "auto" }}>
          {notifications.slice(0, 4).map((n, i) => <NotifRow key={i} n={n} index={i} mode="panel" />)}
        </div>
        <PanelFooter onViewAll={() => setModalOpen(true)} />
      </div>
      {modal}
    </>
  );
};

export default NotificationManager;
